import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { li as LiMotion } from 'motion/react-client';
import { BottomSheet, BottomSheetContentLayout, Box, Button, Callout, Column, ConfirmationModal, POPOVER_ARROW_ICON_WIDTH, PopoverArrowIcon, Row, TextButton, Typography } from 'seagull';
import { Bank, Bulb, CreditCard, Plus, PlusCircle } from 'seagull/icons';
import { useResponsive } from '~/client-flow/contexts';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useDisconnectPaymentMethod } from '~/hooks/useDisconnectPaymentMethod';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { PlaidMode, usePlaidState } from '~/hooks/usePlaidState';
import { useRemovePaymentMethod } from '~/hooks/useRemovePaymentMethod';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { paymentMethodModel } from '~/models';
import { RelationshipProgressStatus } from '~/models/relationship';
import { BusinessChannelEventBinder, PaymentMethodEventType, PusherEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import LoadingIndicator from '../LoadingIndicator';
import RestfulWrapper from '../RestfulWrapper/RestfulWrapper';
import BankAccountPanel from './BankAccountPanel';
import ConnectCreditCard from './ConnectCreditCard';
import ConnectPlaid from './ConnectPlaid';
import CreditCardPanel from './CreditCardPanel';
import { getReadonlyWarning } from './getReadonlyWarning';
import styles from './ManagePaymentMethods.module.scss';
import { doPaymentMethodAction, fetchPaymentMethods, getRequiresPMActionConfirmation, isSelectablePaymentMethod, shouldPromptConnectAfterAdding } from './ManagePaymentMethods.utils';
import PaymentMethodOption from './PaymentMethodOption';
import { PaymentMethodPanelSkeleton } from './PaymentMethodPanel';
import { useInviteMemberModal } from './useInviteMemberModal';
var SelectPaymentMethodOption = /*#__PURE__*/ function(SelectPaymentMethodOption) {
    SelectPaymentMethodOption["None"] = "none";
    SelectPaymentMethodOption["Plaid"] = "plaid";
    SelectPaymentMethodOption["CreditCard"] = "creditCard";
    return SelectPaymentMethodOption;
}(SelectPaymentMethodOption || {});
function getCreditCardFeeLabel(options) {
    const { canVendorControlPM, showCreditCardFee, isVendorCoveringFees, texts } = options;
    if (canVendorControlPM) {
        return isVendorCoveringFees ? texts.creditCardFeeByYou() : texts.creditCardFeeByClient();
    }
    if (showCreditCardFee) {
        return texts.creditCardFeePercent();
    }
    return isVendorCoveringFees ? texts.feePaidByVendor() : texts.noFee();
}
export function ActualManagePaymentMethods(props) {
    const { vendorId, customerId, relationshipId, isPreview, isMemberClient, isSample, showCreditCardFee, selectedFundingSourceId, isVendorCoveringFees, isProposal = false, updateProgress: updateProgressProp, isRefreshing, isTerminatedAgreement = false, isInviteMemberDisabled, hideInviteMemberHint = false, isAddingAnotherPaymentMethod, hideExpandableAddPmButton, onSelectPaymentMethod, onPaymentMethodUpdated, setIsAddingAnotherPaymentMethod } = props;
    const updateProgress = updateProgressProp !== null && updateProgressProp !== void 0 ? updateProgressProp : isProposal;
    const { t: texts } = useLocaleKeys('components.managePaymentMethods');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { CLIENT_ONBOARDING_NEW_FLOW } = useFeatureFlags();
    const { isMobile } = useResponsive();
    const bottomSheetVariant = isProposal ? 'branded' : undefined;
    const { apiGatewayHost } = useStore((state)=>state.session);
    const isVendor = useStore(selectors.user.isVendor);
    const userRole = useStore(selectors.user.role);
    const isVendorViewOnly = isVendor && !!props.isAdHoc && !isProposal;
    const isClientAdHoc = !!props.isAdHoc && !isVendor;
    const canVendorControlPM = isVendor && !isProposal && !props.isAdHoc;
    const creditCardFeeLabel = getCreditCardFeeLabel({
        canVendorControlPM,
        showCreditCardFee,
        isVendorCoveringFees,
        texts
    });
    const requiresPMActionConfirmation = getRequiresPMActionConfirmation(isVendor, userRole, isProposal);
    const { removePaymentMethodWithConfirmation, removePaymentMethod } = useRemovePaymentMethod();
    const disconnectPaymentMethod = useDisconnectPaymentMethod();
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const [arrowLeft, setArrowLeft] = useState(0);
    const [achButtonEl, setAchButtonEl] = useState(null);
    const [ccButtonEl, setCcButtonEl] = useState(null);
    const [selectedOption, setSelectedOption] = useState("none");
    const [isAddAnotherSheetOpen, setIsAddAnotherSheetOpen] = useState(false);
    const [addAnotherSheetStep, setAddAnotherSheetStep] = useState('selection');
    const [isPaymentMethodInMotion, setIsPaymentMethodInMotion] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState(props.paymentMethods);
    useEffect(()=>setPaymentMethods(props.paymentMethods), [
        props.paymentMethods
    ]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(()=>{
        if (isProposal) {
            var _paymentMethods_;
            return selectedFundingSourceId || ((_paymentMethods_ = paymentMethods[0]) === null || _paymentMethods_ === void 0 ? void 0 : _paymentMethods_.id) || null;
        } else {
            return selectedFundingSourceId || null;
        }
    });
    const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(paymentMethods.length === 0);
    const [fundingSourceIdsAdded, setFundingSourceIdsAdded] = useState([]);
    const selectedPM = paymentMethods.find((pm)=>pm.id === selectedPaymentMethod);
    const selectedPMRequiresVerification = !!selectedPM && paymentMethodModel.requiresVerification(selectedPM);
    useEffect(()=>{
        if (selectedPaymentMethod && isProposal) {
            onSelectPaymentMethod && onSelectPaymentMethod(selectedPM);
        } else {
            onSelectPaymentMethod && onSelectPaymentMethod();
        }
    }, [
        selectedPaymentMethod,
        isProposal,
        onSelectPaymentMethod,
        selectedPM
    ]);
    useEffect(()=>{
        if (isAddingAnotherPaymentMethod) {
            setIsAddingPaymentMethod(isAddingAnotherPaymentMethod);
            if (!isMobile) {
                setSelectedOption("creditCard");
            }
        }
    }, [
        isAddingAnotherPaymentMethod,
        isMobile
    ]);
    useEffect(()=>{
        if (isAddingPaymentMethod && selectedOption === "none" && !isMobile) {
            setSelectedOption("creditCard");
        }
    }, [
        isAddingPaymentMethod,
        selectedOption,
        isMobile
    ]);
    useEffect(()=>{
        const applyNewArrowLeft = (buttonEl)=>{
            const { offsetLeft, clientWidth } = buttonEl;
            const arrowLeft = offsetLeft + clientWidth / 2 - POPOVER_ARROW_ICON_WIDTH / 2;
            setArrowLeft(arrowLeft);
        };
        if (selectedOption === "plaid" && achButtonEl) {
            applyNewArrowLeft(achButtonEl);
        } else if (selectedOption === "creditCard" && ccButtonEl) {
            applyNewArrowLeft(ccButtonEl);
        }
    }, [
        selectedOption,
        achButtonEl,
        ccButtonEl
    ]);
    useEffect(()=>{
        if (paymentMethods.length === 0) {
            // This will happen after removing the only payment-method.
            setIsAddingPaymentMethod(true);
        }
    }, [
        paymentMethods
    ]);
    const disabledReason = getReadonlyWarning({
        isPreview,
        isMemberClient,
        isSampleRelationship: isSample
    });
    const sortedPaymentMethods = useMemo(()=>{
        return paymentMethods.slice().sort((pmA, pmB)=>{
            if (!selectedPaymentMethod) return 0;
            if (pmB.id === selectedPaymentMethod) return 1;
            if (pmA.id === selectedPaymentMethod) return -1;
            return 0;
        });
    }, [
        paymentMethods,
        selectedPaymentMethod
    ]);
    async function refresh() {
        const result = await props.refresh();
        setPaymentMethods(result.paymentMethods);
        return result;
    }
    async function onPlaidSuccess(account, paymentMethodAction) {
        const fundingSourceId = account.id;
        if (updateProgress) {
            api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
        }
        logBI(10807);
        if (paymentMethodAction.type === 'replace') {
            const pmToReplace = paymentMethods.find((pm)=>pm.id === paymentMethodAction.fundingSourceId);
            setPaymentMethods((prev)=>prev.filter((pm)=>pm.id !== paymentMethodAction.fundingSourceId));
            removePaymentMethod({
                fundingSourceId: paymentMethodAction.fundingSourceId,
                vendorId,
                customerId
            }).then((result)=>{
                if (!result.success && pmToReplace) {
                    setPaymentMethods((prev)=>[
                            ...prev,
                            pmToReplace
                        ]);
                    overlayContext.showErrorToast(texts.failedToRemovePaymentMethod(), 'failed-remove-pm-toast');
                }
            });
        }
        setIsAddingPaymentMethod(false);
        setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
        if (isAddAnotherSheetOpen) closeAddAnotherSheet();
        setFundingSourceIdsAdded(fundingSourceIdsAdded.concat(fundingSourceId));
    // The rest of the flow is done after receiving a paymentMethodEvent in the subscriber callback below.
    }
    const connectPaymentMethodModal = async (paymentMethod)=>{
        await overlayContext.showModal({
            biName: 'manage_payment_methods_connect_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'manage_payment_methods_1_confirm'
                        },
                        secondaryButton: {
                            name: 'manage_payment_methods_1_cancel'
                        }
                    },
                    title: texts.connectTitle(),
                    testId: "connect-payment-method-modal",
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.connectPaymentMethodQuestion()
                    }),
                    onPrimaryButtonClick: ()=>{
                        dismissModal(true);
                        onSelect(paymentMethod);
                    },
                    onSecondaryButtonClick: ()=>{
                        dismissModal(false);
                    },
                    primaryButtonLabel: texts.yes(),
                    secondaryButtonLabel: texts.no()
                });
            }
        });
    };
    async function onPlaidVerify(fundingSourceId) {
        const { paymentMethods } = await refresh();
        const pm = paymentMethods.find((paymentMethod)=>paymentMethod.id === fundingSourceId);
        if (pm) {
            if (shouldSelectAfterAddingPM(paymentMethods)) {
                onSelect(pm);
            } else {
                await connectPaymentMethodModal(pm);
            }
        }
    }
    async function onConnectCreditCard(fundingSourceId) {
        setIsAddingPaymentMethod(false);
        setIsAddingAnotherPaymentMethod && setIsAddingAnotherPaymentMethod(false);
        if (isAddAnotherSheetOpen) closeAddAnotherSheet();
        const { paymentMethods } = await refresh();
        const pm = paymentMethods.find((paymentMethod)=>paymentMethod.id === fundingSourceId);
        if (pm) {
            if (shouldSelectAfterAddingPM(paymentMethods)) {
                onSelect(pm);
            } else {
                await connectPaymentMethodModal(pm);
            }
        }
    }
    async function onSelect(paymentMethod) {
        if (isProposal) {
            setSelectedPaymentMethod(paymentMethod.id);
        } else {
            overlayContext.startLoader();
            const { success } = await handlePromise(api.relationship.changePaymentMethod(relationshipId, paymentMethod.id));
            if (!success) {
                overlayContext.showErrorToast(texts.failedToConnectPaymentMethod(), 'change-pm-error-toast');
            } else {
                await refresh();
                setSelectedPaymentMethod(paymentMethod.id);
                overlayContext.showSuccessToast(texts.paymentMethodChangedSuccess(), 'change-pm-success-toast');
            }
            overlayContext.stopLoader();
        }
        setIsPaymentMethodInMotion(true);
    }
    function openOnBehalfModal(onConfirmation, paymentMethod) {
        const action = paymentMethod ? 'set' : 'add';
        overlayContext.showModal({
            biName: 'manage_payment_methods_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'manage_payment_methods_2_confirm'
                        },
                        secondaryButton: {
                            name: 'manage_payment_methods_2_cancel'
                        }
                    },
                    title: texts.addOnBehalfTitle({
                        action: paymentMethod ? 'Setting' : 'Adding'
                    }),
                    textAlignCenter: false,
                    content: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ _jsx(Typography, {
                            children: texts.onBehalfContent({
                                action
                            })
                        })
                    }),
                    onPrimaryButtonClick: async ()=>{
                        onConfirmation();
                        dismissModal(true);
                    },
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    primaryButtonLabel: texts.proceed(),
                    secondaryButtonLabel: commonButtons.back(),
                    primaryButtonTestId: "proceed-to-".concat(action, "-PM"),
                    secondaryButtonTestId: "cancel-".concat(action, "-PM"),
                    testId: "".concat(action, "-on-behalf-modal")
                });
            }
        });
    }
    function addAnotherPaymentMethod() {
        if (CLIENT_ONBOARDING_NEW_FLOW && isMobile && paymentMethods.length > 0) {
            setAddAnotherSheetStep('selection');
            setIsAddAnotherSheetOpen(true);
        } else {
            setSelectedOption(isMobile && CLIENT_ONBOARDING_NEW_FLOW ? "none" : "creditCard");
            setIsAddingPaymentMethod(true);
        }
    }
    function closeAddAnotherSheet() {
        setIsAddAnotherSheetOpen(false);
        setAddAnotherSheetStep('selection');
    }
    const sheetPlaidLink = usePlaidState({
        mode: {
            type: PlaidMode.Customer,
            vendorId,
            customerId,
            relationshipId
        },
        onSuccess (account) {
            onPlaidSuccess(account, {
                type: 'add'
            });
        }
    });
    async function onPaymentMethodPushEvent(param) {
        let { payload } = param;
        const paymentMethod = payload.paymentMethod;
        if (payload.type === PaymentMethodEventType.PaymentMethodUpdated && onPaymentMethodUpdated) {
            onPaymentMethodUpdated();
        }
        const { paymentMethods: newPaymentMethods } = await refresh();
        if (payload.type === PaymentMethodEventType.FailedToAddPaymentMethod) {
            if (!paymentMethod) return;
            if (fundingSourceIdsAdded.some((id)=>id === paymentMethod.fundingSourceId)) {
                setFundingSourceIdsAdded(fundingSourceIdsAdded.filter((id)=>id !== paymentMethod.fundingSourceId));
                overlayContext.showErrorToast(texts.failedToAddDuplicateBankAccount(), 'add-duplicate-pm-toast');
            } else if (paymentMethods.some((pm)=>pm.id === paymentMethod.fundingSourceId && paymentMethodModel.isPendingManualVerification(pm))) {
                overlayContext.showErrorToast(texts.failedToVerifyDuplicateBankAccount(), 'verify-duplicate-pm-toast');
            }
        } else if (payload.type === PaymentMethodEventType.PaymentMethodAdded && fundingSourceIdsAdded.length) {
            const addedPM = newPaymentMethods.find((pm)=>fundingSourceIdsAdded.includes(pm.id));
            if (addedPM) {
                if (shouldSelectAfterAddingPM(newPaymentMethods)) {
                    onSelect(addedPM);
                } else {
                    if (shouldPromptConnectAfterAdding(addedPM)) {
                        await connectPaymentMethodModal(addedPM);
                    }
                }
                setFundingSourceIdsAdded(fundingSourceIdsAdded.filter((id)=>addedPM.id !== id));
            }
        }
    }
    function shouldSelectAfterAddingPM(newPaymentMethods) {
        return newPaymentMethods.length === 1 || isProposal || selectedPMRequiresVerification;
    }
    const connectPaymentMethodProps = {
        relationshipId,
        vendorId,
        customerId: customerId,
        disabledReason,
        updateProgress,
        isProposal,
        doPaymentMethodAction: (action, paymentMethod)=>doPaymentMethodAction(action, requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
    };
    const showConnectAch = isMobile && CLIENT_ONBOARDING_NEW_FLOW || selectedOption === "plaid" || addAnotherSheetStep === 'ach';
    const connectAch = showConnectAch && /*#__PURE__*/ _jsx(ConnectPlaid, _object_spread_props(_object_spread({}, connectPaymentMethodProps), {
        onSuccess: onPlaidSuccess
    }));
    const showConnectCreditCard = isMobile && CLIENT_ONBOARDING_NEW_FLOW || selectedOption === "creditCard" || selectedOption === "none" && !isMobile || addAnotherSheetStep === 'card';
    const connectCreditCard = showConnectCreditCard && /*#__PURE__*/ _jsx(ConnectCreditCard, _object_spread_props(_object_spread({}, connectPaymentMethodProps), {
        onConnect: onConnectCreditCard
    }));
    const { openInviteMemberModal } = useInviteMemberModal();
    const inviteHintTop = !hideInviteMemberHint && /*#__PURE__*/ _jsxs(Row, {
        gap: 12,
        alignItems: "center",
        className: cn(styles.inviteMemberHintPrimary, {
            [styles.inviteMemberHintOnPrimaryBg]: isProposal
        }),
        testId: "invite-member-hint-top",
        children: [
            /*#__PURE__*/ _jsx(Bulb, {
                className: styles.inviteMemberHintIcon
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraph1",
                as: "span",
                children: [
                    texts.needHelpAddingPm(),
                    ' ',
                    /*#__PURE__*/ _jsx(TextButton, {
                        skin: "primary",
                        underline: "none",
                        weight: "normal",
                        intent: Intent.InviteTeamMember,
                        disabled: isInviteMemberDisabled,
                        onClick: openInviteMemberModal,
                        testId: "invite-member",
                        bi: {
                            name: 'invite_team_member'
                        },
                        children: texts.inviteTeamMember()
                    })
                ]
            })
        ]
    });
    const inviteCallout = !hideInviteMemberHint && /*#__PURE__*/ _jsx(Box, {
        className: cn(styles.inviteMemberCallout, {
            [styles.isMobile]: isMobile
        }),
        style: {
            maxWidth: isMobile ? '100%' : 560
        },
        children: /*#__PURE__*/ _jsxs(Callout, {
            intent: "info",
            testId: "invite-member-callout",
            children: [
                texts.needHelpAddingPm(),
                ' ',
                /*#__PURE__*/ _jsx(TextButton, {
                    intent: Intent.InviteTeamMember,
                    disabled: isInviteMemberDisabled,
                    onClick: openInviteMemberModal,
                    testId: "invite-member",
                    bi: {
                        name: 'invite_team_member'
                    },
                    children: texts.inviteTeamMember()
                })
            ]
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            CLIENT_ONBOARDING_NEW_FLOW && inviteHintTop,
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "mgmt",
                eventName: PusherEvent.PaymentMethod,
                callback: onPaymentMethodPushEvent
            }),
            (!isRefreshing || sortedPaymentMethods.length > 0 || fundingSourceIdsAdded.length > 0) && (sortedPaymentMethods.length > 0 || fundingSourceIdsAdded.length > 0) && /*#__PURE__*/ _jsxs(Box, {
                as: "ul",
                className: cn(styles.existingPaymentMethods, hideExpandableAddPmButton && styles.noAddPaymentTextButton),
                children: [
                    sortedPaymentMethods.map((paymentMethod)=>{
                        const isSelected = paymentMethod.id === selectedPaymentMethod;
                        const disconnectable = canVendorControlPM && isSelected || isClientAdHoc;
                        const commonProps = {
                            key: paymentMethod.id,
                            isProposal,
                            isSelected,
                            isPaymentMethodInMotion,
                            apiGatewayHost,
                            removable: !isVendorViewOnly && !isTerminatedAgreement && (!isSelected || paymentMethodModel.failedVerification(paymentMethod)) && !isProposal && paymentMethod.isRemovable && !isMemberClient,
                            paymentMethodIsRemovable: paymentMethod.isRemovable,
                            selectable: !isVendorViewOnly && isSelectablePaymentMethod(paymentMethod, isTerminatedAgreement, isSelected, isProposal, selectedPMRequiresVerification, paymentMethods.length === 1),
                            disconnectable,
                            isAdHoc: props.isAdHoc,
                            verifyable: !isTerminatedAgreement,
                            hasPermissionToVerify: !isMemberClient,
                            onRemove: ()=>removePaymentMethodWithConfirmation({
                                    fundingSourceId: paymentMethod.id,
                                    vendorId,
                                    customerId: props.customerId,
                                    isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
                                    last4: paymentMethod.last4,
                                    async onSuccess () {
                                        await refresh();
                                        onPaymentMethodUpdated && onPaymentMethodUpdated();
                                    }
                                }),
                            onDisconnect: ()=>disconnectPaymentMethod({
                                    relationshipId,
                                    isCreditCard: paymentMethodModel.isCreditCard(paymentMethod),
                                    last4: paymentMethod.last4,
                                    async onSuccess () {
                                        if (props.isAdHoc) {
                                            await refresh();
                                        }
                                        setSelectedPaymentMethod(null);
                                    },
                                    performDisconnect: props.isAdHoc ? ()=>removePaymentMethod({
                                            fundingSourceId: paymentMethod.id,
                                            vendorId,
                                            customerId
                                        }) : undefined
                                }),
                            onSelect: ()=>doPaymentMethodAction(()=>onSelect(paymentMethod), requiresPMActionConfirmation, openOnBehalfModal, paymentMethod)
                        };
                        return /*#__PURE__*/ _jsx(LiMotion, {
                            layout: "position",
                            transition: {
                                type: 'tween',
                                duration: 0.3
                            },
                            style: {
                                zIndex: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.id) === selectedPaymentMethod ? 1 : 0
                            },
                            onLayoutAnimationComplete: ()=>{
                                setIsPaymentMethodInMotion(false);
                            },
                            children: paymentMethodModel.isBankAccount(paymentMethod) ? /*#__PURE__*/ _jsx(BankAccountPanel, _object_spread_props(_object_spread({}, commonProps), {
                                bankAccount: paymentMethod,
                                relationshipId: relationshipId,
                                customerId: customerId,
                                vendorId: vendorId,
                                onSuccess: onPlaidSuccess,
                                onVerify: onPlaidVerify
                            })) : /*#__PURE__*/ _jsx(CreditCardPanel, _object_spread_props(_object_spread({}, commonProps), {
                                creditCard: paymentMethod
                            }))
                        }, paymentMethod.id);
                    }),
                    fundingSourceIdsAdded.map((_, idx)=>/*#__PURE__*/ _jsx(PaymentMethodPanelSkeleton, {}, idx))
                ]
            }),
            !isTerminatedAgreement && paymentMethods.length > 0 && !hideExpandableAddPmButton && (isAddingPaymentMethod ? CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/ _jsx(Typography, {
                className: cn(styles.addPaymentMethod, isProposal && styles.addPaymentMethodOnPrimaryBg),
                variant: "paragraph2",
                weight: "normal",
                skin: "secondary",
                children: texts.addAnotherPaymentMethod()
            }) : /*#__PURE__*/ _jsx(Typography, {
                className: cn(styles.addPaymentMethod, styles.addPaymentMethodLegacy),
                skin: "secondary",
                children: texts.addAnotherPaymentMethod()
            }) : CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(TextButton, {
                    className: cn(styles.addPaymentMethod, isProposal && styles.addPaymentMethodOnPrimaryBg),
                    variant: "paragraph2",
                    weight: "normal",
                    underline: "none",
                    prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 18
                    }),
                    testId: "manage-payment-methods-add-payment-method",
                    intent: Intent.AddPaymentMethod,
                    onClick: addAnotherPaymentMethod,
                    bi: {
                        name: 'add_another_payment_method'
                    },
                    children: texts.addAnotherPaymentMethod()
                })
            }) : /*#__PURE__*/ _jsx(TextButton, {
                className: cn(styles.addPaymentMethod, styles.addPaymentMethodLegacy),
                prefixIcon: /*#__PURE__*/ _jsx(Plus, {}),
                testId: "manage-payment-methods-add-payment-method",
                intent: Intent.AddPaymentMethod,
                onClick: addAnotherPaymentMethod,
                bi: {
                    name: 'add_another_payment_method'
                },
                children: texts.addAnotherPaymentMethod()
            })),
            !isTerminatedAgreement && isAddingPaymentMethod && (!CLIENT_ONBOARDING_NEW_FLOW || !isMobile || paymentMethods.length === 0 || !!hideExpandableAddPmButton) && /*#__PURE__*/ _jsxs(Box, {
                className: cn(styles.options, {
                    [styles.isMobile]: isMobile,
                    [styles.ccNoFee]: !showCreditCardFee,
                    [styles.optionsNewUi]: CLIENT_ONBOARDING_NEW_FLOW,
                    [styles.optionsClientOnboarding]: CLIENT_ONBOARDING_NEW_FLOW && isProposal && !isMobile
                }),
                style: !isMobile ? {
                    maxWidth: 700
                } : undefined,
                children: [
                    !CLIENT_ONBOARDING_NEW_FLOW ? /*#__PURE__*/ _jsxs("div", {
                        className: styles.optionsButtonsRow,
                        children: [
                            /*#__PURE__*/ _jsx(PaymentMethodOption, {
                                className: cn(styles.optionsOption, {
                                    [styles.optionBgAgreementMinisite]: !isProposal
                                }),
                                text: texts.creditCard(),
                                parenthesisText: creditCardFeeLabel,
                                icon: /*#__PURE__*/ _jsx(CreditCard, {}),
                                selected: selectedOption === "creditCard",
                                testId: "manage-payment-methods-credit-card",
                                ref: setCcButtonEl,
                                onOptionClick: ()=>setSelectedOption("creditCard"),
                                bi: {
                                    name: 'select_credit_card'
                                }
                            }),
                            /*#__PURE__*/ _jsx(PaymentMethodOption, {
                                className: cn(styles.optionsOption, {
                                    [styles.optionBgAgreementMinisite]: !isProposal
                                }),
                                text: texts.secureAch(),
                                parenthesisText: canVendorControlPM ? '' : texts.noFee(),
                                icon: /*#__PURE__*/ _jsx(Bank, {}),
                                selected: selectedOption === "plaid",
                                testId: "manage-payment-methods-secure-ach",
                                ref: setAchButtonEl,
                                onOptionClick: ()=>setSelectedOption("plaid"),
                                bi: {
                                    name: 'select_ach'
                                }
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(PaymentMethodOption, {
                                className: cn(styles.optionsOption, {
                                    [styles.optionBgAgreementMinisite]: !isProposal,
                                    [styles.optionBgClientOnboarding]: isProposal
                                }),
                                text: CLIENT_ONBOARDING_NEW_FLOW ? texts.card() : texts.creditCard(),
                                parenthesisText: creditCardFeeLabel,
                                icon: /*#__PURE__*/ _jsx(CreditCard, {}),
                                selected: selectedOption === "creditCard",
                                testId: "manage-payment-methods-credit-card",
                                ref: setCcButtonEl,
                                onOptionClick: ()=>setSelectedOption("creditCard"),
                                mobileView: isMobile,
                                bi: {
                                    name: 'select_credit_card'
                                },
                                sheetTitle: isMobile && CLIENT_ONBOARDING_NEW_FLOW ? texts.card() : undefined,
                                onSheetClose: isMobile && CLIENT_ONBOARDING_NEW_FLOW ? ()=>setSelectedOption("none") : undefined,
                                bottomSheetVariant: bottomSheetVariant,
                                children: connectCreditCard
                            }),
                            /*#__PURE__*/ _jsx(PaymentMethodOption, {
                                className: cn(styles.optionsOption, {
                                    [styles.optionBgAgreementMinisite]: !isProposal,
                                    [styles.optionBgClientOnboarding]: isProposal
                                }),
                                text: texts.secureAch(),
                                parenthesisText: canVendorControlPM ? '' : texts.noFee(),
                                icon: /*#__PURE__*/ _jsx(Bank, {}),
                                selected: selectedOption === "plaid",
                                testId: "manage-payment-methods-secure-ach",
                                ref: setAchButtonEl,
                                onOptionClick: ()=>setSelectedOption("plaid"),
                                mobileView: isMobile,
                                bi: {
                                    name: 'select_ach'
                                },
                                sheetTitle: isMobile && CLIENT_ONBOARDING_NEW_FLOW ? texts.secureAch() : undefined,
                                onSheetClose: isMobile && CLIENT_ONBOARDING_NEW_FLOW ? ()=>setSelectedOption("none") : undefined,
                                bottomSheetVariant: bottomSheetVariant,
                                children: connectAch
                            })
                        ]
                    }),
                    (!isMobile || !CLIENT_ONBOARDING_NEW_FLOW) && /*#__PURE__*/ _jsxs(Box, {
                        className: cn(styles.box, {
                            [styles.boxNewUi]: CLIENT_ONBOARDING_NEW_FLOW,
                            [styles.boxAgreementMinisite]: !isProposal,
                            [styles.boxClientOnboarding]: isProposal
                        }),
                        children: [
                            !CLIENT_ONBOARDING_NEW_FLOW && /*#__PURE__*/ _jsx(PopoverArrowIcon, {
                                className: styles.arrow,
                                style: {
                                    left: arrowLeft
                                }
                            }),
                            compact([
                                connectAch,
                                connectCreditCard
                            ])
                        ]
                    })
                ]
            }),
            CLIENT_ONBOARDING_NEW_FLOW && isMobile && paymentMethods.length > 0 && !hideExpandableAddPmButton && /*#__PURE__*/ _jsxs(BottomSheet, {
                open: isAddAnotherSheetOpen,
                onClose: closeAddAnotherSheet,
                showBackButton: addAnotherSheetStep !== 'selection',
                onBack: ()=>setAddAnotherSheetStep('selection'),
                testId: "add-another-payment-method-sheet",
                variant: bottomSheetVariant,
                children: [
                    addAnotherSheetStep === 'selection' && /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: {
                            title: texts.addPaymentMethod(),
                            paddingBottom: 16
                        },
                        contentGutter: "m",
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 16,
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    className: styles.addAnotherSheetOption,
                                    onClick: ()=>setAddAnotherSheetStep('card'),
                                    testId: "add-another-sheet-card-option",
                                    bi: {
                                        name: 'select_credit_card'
                                    },
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        gap: 8,
                                        alignItems: "center",
                                        className: styles.addAnotherSheetOptionRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(Box, {
                                                className: styles.addAnotherSheetOptionIcon,
                                                children: /*#__PURE__*/ _jsx(CreditCard, {})
                                            }),
                                            /*#__PURE__*/ _jsx(Box, {
                                                className: styles.addAnotherSheetOptionName,
                                                children: /*#__PURE__*/ _jsx(Typography, {
                                                    as: "span",
                                                    variant: "paragraph1",
                                                    skin: "standard",
                                                    weight: "normal",
                                                    children: texts.card()
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                as: "span",
                                                variant: "paragraph1",
                                                skin: "secondary",
                                                className: styles.addAnotherSheetOptionFee,
                                                children: creditCardFeeLabel
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    className: styles.addAnotherSheetOption,
                                    onClick: ()=>setAddAnotherSheetStep('ach'),
                                    testId: "add-another-sheet-ach-option",
                                    bi: {
                                        name: 'select_ach'
                                    },
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        gap: 8,
                                        alignItems: "center",
                                        className: styles.addAnotherSheetOptionRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(Box, {
                                                className: styles.addAnotherSheetOptionIcon,
                                                children: /*#__PURE__*/ _jsx(Bank, {})
                                            }),
                                            /*#__PURE__*/ _jsx(Box, {
                                                className: styles.addAnotherSheetOptionName,
                                                children: /*#__PURE__*/ _jsx(Typography, {
                                                    as: "span",
                                                    variant: "paragraph1",
                                                    skin: "standard",
                                                    weight: "normal",
                                                    children: texts.secureAch()
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                as: "span",
                                                variant: "paragraph1",
                                                skin: "secondary",
                                                className: styles.addAnotherSheetOptionFee,
                                                children: texts.noFee()
                                            })
                                        ]
                                    })
                                })
                            ]
                        })
                    }),
                    addAnotherSheetStep === 'card' && /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: {
                            title: texts.card(),
                            paddingBottom: 16
                        },
                        contentGutter: "m",
                        children: connectCreditCard
                    }),
                    addAnotherSheetStep === 'ach' && /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: {
                            title: texts.secureAch(),
                            paddingBottom: 16
                        },
                        contentGutter: "m",
                        contentClassName: styles.achSheetContentFullHeight,
                        children: /*#__PURE__*/ _jsx(ConnectPlaid, _object_spread_props(_object_spread({}, connectPaymentMethodProps), {
                            doPaymentMethodAction: ()=>{
                                closeAddAnotherSheet();
                                sheetPlaidLink.open();
                            },
                            onSuccess: onPlaidSuccess
                        }))
                    })
                ]
            }),
            !CLIENT_ONBOARDING_NEW_FLOW && sortedPaymentMethods.length === 0 && inviteCallout
        ]
    });
}
export default function ManagePaymentMethods(props) {
    const { isPreview, isSample, relationshipId, isProposal, vendorId, customerId, initialSelectedPaymentMethodId } = props;
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const fetch = ()=>{
        return fetchPaymentMethods({
            isSample,
            vendorId,
            isPreview,
            businessId,
            isProposal,
            initialSelectedPaymentMethodId,
            clientGetPaymentMethod: ()=>api.paymentMethod.clientGet(vendorId),
            vendorGetPaymentMethod: ()=>api.paymentMethod.vendorGet(customerId),
            getSelectedPaymentMethod: ()=>api.relationship.getSelectedPaymentMethod(relationshipId)
        });
    };
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetch,
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            return /*#__PURE__*/ _jsx(ActualManagePaymentMethods, _object_spread_props(_object_spread({}, props), {
                paymentMethods: data.paymentMethods,
                selectedFundingSourceId: data.selectedFundingSourceId,
                isRefreshing: loading,
                refresh: refresh
            }));
        },
        renderLoader: ()=>/*#__PURE__*/ _jsx(LoadingIndicator, {
                testId: "manage-payment-methods-loading-indicator"
            })
    });
}
